/**
 * Trunnion AI - Replay Controls
 * VCR-style playback controls: play/pause, step forward/back, speed selector,
 * and a step counter. Auto-play is driven by a setInterval whose period is
 * derived from the current speed setting (base interval = 2000ms / speed).
 */

import { useEffect, useRef } from 'react';
import { Play, Pause, SkipBack, SkipForward } from 'lucide-react';
import { useReplayStore } from '@/stores/replayStore';
import { Button } from '@/components/ui/Button';

const SPEED_OPTIONS = [0.5, 1, 2, 4] as const;

/** Base interval in milliseconds at 1x speed */
const BASE_INTERVAL_MS = 2000;

export function ReplayControls() {
  const isPlaying = useReplayStore((s) => s.isPlaying);
  const speed = useReplayStore((s) => s.speed);
  const currentStepIndex = useReplayStore((s) => s.currentStepIndex);
  const totalSteps = useReplayStore((s) => s.steps.length);

  const togglePlay = useReplayStore((s) => s.togglePlay);
  const stepForward = useReplayStore((s) => s.stepForward);
  const stepBack = useReplayStore((s) => s.stepBack);
  const setSpeed = useReplayStore((s) => s.setSpeed);

  // Keep a ref to stepForward so the interval callback always uses the latest version
  const stepForwardRef = useRef(stepForward);
  stepForwardRef.current = stepForward;

  // Auto-play interval
  useEffect(() => {
    if (!isPlaying) return;

    const intervalMs = BASE_INTERVAL_MS / speed;
    const id = setInterval(() => {
      stepForwardRef.current();
    }, intervalMs);

    return () => clearInterval(id);
  }, [isPlaying, speed]);

  const isAtStart = currentStepIndex === 0;
  const isAtEnd = currentStepIndex >= totalSteps - 1;

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-900 px-4 py-2.5">
      {/* Transport controls */}
      <div className="flex items-center gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          icon={<SkipBack className="h-4 w-4" />}
          onClick={stepBack}
          disabled={isAtStart}
          aria-label="Step back"
        />

        <Button
          variant="ghost"
          size="sm"
          icon={
            isPlaying ? (
              <Pause className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4" />
            )
          }
          onClick={togglePlay}
          disabled={totalSteps === 0}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          className={
            isPlaying
              ? 'text-trunnion-gold-500 hover:text-trunnion-gold-400'
              : ''
          }
        />

        <Button
          variant="ghost"
          size="sm"
          icon={<SkipForward className="h-4 w-4" />}
          onClick={stepForward}
          disabled={isAtEnd}
          aria-label="Step forward"
        />
      </div>

      {/* Step counter */}
      <span className="text-xs font-medium text-trunnion-slate-300 tabular-nums whitespace-nowrap">
        Step{' '}
        <span className="text-white">{totalSteps > 0 ? currentStepIndex + 1 : 0}</span>{' '}
        of{' '}
        <span className="text-white">{totalSteps}</span>
      </span>

      {/* Speed selector */}
      <div className="flex items-center gap-1">
        {SPEED_OPTIONS.map((opt) => {
          const isActive = speed === opt;
          return (
            <button
              key={opt}
              onClick={() => setSpeed(opt)}
              className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                isActive
                  ? 'bg-trunnion-gold-500/20 text-trunnion-gold-400 ring-1 ring-trunnion-gold-500/40'
                  : 'text-trunnion-slate-400 hover:text-white hover:bg-trunnion-slate-800'
              }`}
              aria-label={`Set speed to ${opt}x`}
            >
              {opt}x
            </button>
          );
        })}
      </div>
    </div>
  );
}
